/**
 * UserActionEmitter — Bridge from tool executor to EventBus
 *
 * Called from executor.ts after successful tool execution.
 * Infers EngineEventType and AuditEntityType from the tool name.
 */

import type { EngineEvent, EngineEventType, AuditEntityType, AuditAction } from './types';
import { eventBus } from './eventBus';

/** Prefix → EngineEventType mapping */
const ACTION_PREFIX_MAP: Record<string, EngineEventType> = {
  create_: 'entity.created',
  add_: 'entity.created',
  update_: 'entity.updated',
  edit_: 'entity.updated',
  modify_: 'entity.updated',
  set_: 'entity.updated',
  delete_: 'entity.deleted',
  remove_: 'entity.deleted',
  archive_: 'entity.deleted',
  send_: 'user.action',
  search_: 'user.action',
  list_: 'user.action',
  get_: 'user.action',
  find_: 'user.action',
};

/** Prefix → AuditAction mapping */
const ACTION_AUDIT_MAP: Record<string, AuditAction> = {
  create_: 'CREATE' as AuditAction,
  add_: 'CREATE' as AuditAction,
  update_: 'UPDATE' as AuditAction,
  edit_: 'UPDATE' as AuditAction,
  modify_: 'UPDATE' as AuditAction,
  set_: 'UPDATE' as AuditAction,
  delete_: 'DELETE' as AuditAction,
  remove_: 'DELETE' as AuditAction,
  archive_: 'ARCHIVE' as AuditAction,
  send_: 'SEND' as AuditAction,
  search_: 'SEARCH' as AuditAction,
  list_: 'LIST' as AuditAction,
  get_: 'READ' as AuditAction,
  find_: 'SEARCH' as AuditAction,
};

/** Known entity suffixes → AuditEntityType */
const ENTITY_SUFFIX_MAP: Record<string, AuditEntityType> = {
  project: 'project',
  projects: 'project',
  task: 'task',
  tasks: 'task',
  event: 'calendar',
  events: 'calendar',
  calendar: 'calendar',
  contact: 'contact',
  contacts: 'contact',
  invoice: 'invoice',
  invoices: 'invoice',
  email: 'email',
  emails: 'email',
  message: 'message',
  messages: 'message',
  milestone: 'process',
  milestones: 'process',
  time_entry: 'time',
  time: 'time',
  bank: 'bank',
  transaction: 'banktransaction',
  document: 'document',
  attachment: 'attachment',
  tag: 'tag',
  link: 'link',
  payment: 'payment',
  journal: 'journal',
};

/**
 * Infer the event type from a tool name prefix
 */
function inferEventType(toolName: string): EngineEventType {
  for (const [prefix, eventType] of Object.entries(ACTION_PREFIX_MAP)) {
    if (toolName.startsWith(prefix)) {
      return eventType;
    }
  }
  return 'user.action';
}

/**
 * Infer the audit action from a tool name prefix
 */
function inferAction(toolName: string): AuditAction | undefined {
  for (const [prefix, action] of Object.entries(ACTION_AUDIT_MAP)) {
    if (toolName.startsWith(prefix)) {
      return action;
    }
  }
  return undefined;
}

/**
 * Infer the entity type from a tool name suffix
 */
function inferEntityType(toolName: string): AuditEntityType | undefined {
  // Remove the action prefix to get the entity part
  const parts = toolName.split('_');
  if (parts.length < 2) return undefined;

  // Try progressively shorter suffixes
  for (let i = 1; i < parts.length; i++) {
    const suffix = parts.slice(i).join('_');
    if (ENTITY_SUFFIX_MAP[suffix]) {
      return ENTITY_SUFFIX_MAP[suffix];
    }
  }

  // Try last part only
  const lastPart = parts[parts.length - 1];
  if (ENTITY_SUFFIX_MAP[lastPart]) {
    return ENTITY_SUFFIX_MAP[lastPart];
  }

  return undefined;
}

/**
 * Emit an engine event from a tool execution result.
 * Called from executor.ts after successful tool execution.
 *
 * @param toolName - Name of the tool that was executed
 * @param params - Parameters passed to the tool
 * @param result - Tool execution result
 * @param userId - ID of the user who triggered the action
 */
export function emitFromToolResult(
  toolName: string,
  params: Record<string, unknown>,
  result: { success: boolean; data?: unknown; tool: string },
  userId: string
): void {
  // Only emit on success
  if (!result.success) return;

  const eventType = inferEventType(toolName);
  const entityType = inferEntityType(toolName);
  const action = inferAction(toolName);

  // Extract entity ID from params or result
  const entityId = (params.id as string)
    || (params.projectId as string)
    || (params.taskId as string)
    || (params.eventId as string)
    || (params.contactId as string)
    || (typeof result.data === 'object' && result.data !== null ? (result.data as Record<string, unknown>).id as string : undefined)
    || undefined;

  const event: EngineEvent = {
    id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: eventType,
    entityType,
    entityId,
    action,
    data: {
      toolName,
      params,
      result: result.data,
    },
    timestamp: new Date().toISOString(),
    userId,
    source: 'user',
  };

  // Fire and forget — don't block the tool executor
  eventBus.emit(event).catch((err) => {
    console.error('[UserActionEmitter] Failed to emit event:', err);
  });
}
